import type { ITalkCallsEvent } from '@zyno-platform/shared';

import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { handleErrorAndAlert } from '@zyno-io/vue-foundation';
import { chunk } from 'lodash';
import { computed, onMounted, onUnmounted, ref, watch, type WatchStopHandle } from 'vue';

import { createLiveInvalidation } from '@/modules/talk/shared/composables/use-live-invalidation';
import { useTalkWorkstationExtension } from '@/modules/talk/shared/store';
import { SSETalkAccessChannel } from '@/modules/talk/sse';
import { TalkCdrApi, type IExtensionLiveCallResponse, type ILiveCallsListResponse } from '@/openapi-clients-generated/talk';
import { DataCache } from '@/shared/data/cache';
import { Logger } from '@/shared/services/logger';

const log = Logger.createScoped('ExtensionLiveCalls');

type LiveAvailability = ILiveCallsListResponse['availability'];

/** The Talk API caps `extensionIds` per `/cdr/live` request — see the server's MAX_LIVE_EXTENSION_IDS. */
const MAX_LIVE_EXTENSION_IDS = 50;

function sameIds(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every(id => b.includes(id));
}

/**
 * Merges the per-chunk `/cdr/live` responses (see `MAX_LIVE_EXTENSION_IDS`) into one. Availability is
 * "any chunk available wins": in the pathological case where chunks disagree (a transient hiccup on
 * one request but not another, since each chunk is an independent request), showing whatever live
 * data we did get beats blanking the whole feed out over one flaky chunk. Failing that, 'unsupported'
 * (a real, permanent backend limitation) outranks a transient 'unavailable'.
 */
function mergeLiveCallsResponses(responses: ILiveCallsListResponse[]): ILiveCallsListResponse | undefined {
    if (!responses.length) return undefined;
    const availability: LiveAvailability = responses.some(r => r.availability === 'available')
        ? 'available'
        : responses.some(r => r.availability === 'unsupported')
          ? 'unsupported'
          : 'unavailable';
    return {
        availability,
        calls: responses.flatMap(r => r.calls),
        deniedExtensionIds: [...new Set(responses.flatMap(r => r.deniedExtensionIds))]
    };
}

/**
 * Ref-counted live-call feed for the dock/dialer phone surfaces (`docs/talk-workstation-extension-plan.md`
 * §5). Watches exactly one set of extensions at a time — the workstation-linked extension on a shared
 * workstation, or the user's starred/primary extensions everywhere else (dedicated workstation or
 * browser) — never both, mirroring the plan's §0a shared-vs-dedicated split.
 *
 * Backed by `GET /cdr/live`, kept fresh with the same SSE-invalidation + 60s-safety-net protocol as
 * parking (`createLiveInvalidation`), but the feed only goes live while the watched set is non-empty —
 * most sessions (no starred extension, not on a shared workstation) never subscribe or poll at all.
 */
/** Exported (not just the singleton below) so specs can construct isolated instances. */
export class ExtensionLiveCallsManager {
    // Constructing this singleton must stay free of side effects (see useTalkWorkstationExtension's
    // own note) — every DataCache access below is lazy, inside the computed, never at field-init time.
    private readonly workstationExtension = useTalkWorkstationExtension();

    private readonly watchedIds = computed<string[]>(() => {
        // `blocksPersonalFallback` also covers a workstation session whose link hasn't resolved yet —
        // fail CLOSED (watch nothing) rather than briefly watching the personal starred/primary set.
        if (this.workstationExtension.blocksPersonalFallback.value) {
            const id = this.workstationExtension.workstationExtensionId.value;
            return id ? [id] : [];
        }
        return (DataCache.getRef('talkSubscribedNumbers').value ?? [])
            .filter(s => s.objectType === 'extension' && (s.starred || s.primary))
            .map(s => s.objectId);
    });

    private readonly callsRef = ref<IExtensionLiveCallResponse[]>([]);
    private readonly availabilityRef = ref<LiveAvailability>('available');
    /** callIds (and rootCallIds) from the latest successful fetch — lets `keyFor` ignore SSE noise for calls we don't hold. */
    private knownCallIds = new Set<string>();
    /**
     * Watched ids the server has already denied (a starred extension whose grant was revoked, etc.) —
     * excluded from the request until the watched set itself changes (see `onWatchedIdsChanged`, the
     * only place that clears this). The linked extension is never added here: a denied link instead
     * reloads `talkWorkstationExtension` below so it can heal or clear itself.
     */
    private deniedNonLinkedIds = new Set<string>();
    /**
     * True once an 'unsupported' answer (e.g. FusionPBX) has stopped the SSE+polling invalidation —
     * that answer cannot change mid-session for the same watched set, so there's no point re-asking
     * every interval. Cleared (and polling resumed) when the watched set actually changes, or on a
     * `talk.access` reload.
     */
    private pollingSuspended = false;

    private refCount = 0;
    private liveActive = false;
    /** Bumped on every fetch; a response is applied only if it's still the latest — last-writer-wins. */
    private issue = 0;
    private stopWatchedIds?: WatchStopHandle;
    private readonly onTalkAccess = () => {
        if (!this.pollingSuspended) return;
        this.resumeSuspendedPolling();
        void this.refresh();
    };

    private readonly invalidation = createLiveInvalidation({
        keyFor: event => this.keyFor(event),
        onInvalidate: () => this.refresh(),
        onResync: () => this.refresh(),
        intervalMs: 60_000
    });

    readonly calls = computed(() => this.callsRef.value);
    readonly availability = computed(() => this.availabilityRef.value);

    /** Hold the feed open for the lifetime of the calling component (the dock, the dialer). */
    use() {
        onMounted(() => this.acquire());
        onUnmounted(() => this.release());
    }

    async decline(call: Pick<IExtensionLiveCallResponse, 'callId' | 'extensionId'>) {
        try {
            await TalkCdrApi.postCdrDecline({ path: { callId: call.callId }, body: { extensionId: call.extensionId } });
        } catch (err) {
            // User-initiated, so unlike the background refresh below this needs to actually reach the
            // user — accepting the resulting duplicate Sentry report (the openapi client's onError hook
            // already reported the underlying request failure) in exchange for that.
            handleErrorAndAlert(err, { cause: 'extension-live-calls:decline' });
            return;
        }
        // Whether it actually declined or was already past ringing ('notRinging'), the list is stale.
        void this.refresh();
    }

    private acquire() {
        this.refCount += 1;
        if (this.refCount > 1) return;
        this.stopWatchedIds = watch(this.watchedIds, (ids, oldIds) => this.onWatchedIdsChanged(ids, oldIds), { immediate: true });
        SSETalkAccessChannel.subscribe(this.onTalkAccess);
    }

    private release() {
        this.refCount = Math.max(0, this.refCount - 1);
        if (this.refCount > 0) return;
        this.stopWatchedIds?.();
        this.stopWatchedIds = undefined;
        SSETalkAccessChannel.unsubscribe(this.onTalkAccess);
        this.stopLive();
    }

    private onWatchedIdsChanged(ids: string[], oldIds: string[] | undefined) {
        if (!ids.length) {
            this.stopLive();
            return;
        }
        if (!this.liveActive) {
            this.startLive();
            return;
        }
        if (oldIds && sameIds(ids, oldIds)) return;
        // A real change in the watched set (e.g. the link changed, a starred extension was added)
        // invalidates both freezes below: an id the server had denied might not even be in the new
        // set, and an 'unsupported' answer might have been for a now-abandoned link — then refetches
        // immediately rather than waiting for the next invalidation/resync.
        this.deniedNonLinkedIds.clear();
        this.resumeSuspendedPolling();
        void this.refresh();
    }

    private resumeSuspendedPolling() {
        if (!this.pollingSuspended) return;
        this.pollingSuspended = false;
        this.invalidation.start();
        this.invalidation.ready();
    }

    private startLive() {
        if (this.liveActive) return;
        this.liveActive = true;
        this.invalidation.start();
        void this.initialLoad();
    }

    private stopLive() {
        if (this.liveActive) {
            this.liveActive = false;
            this.invalidation.stop();
        }
        this.deniedNonLinkedIds.clear();
        this.pollingSuspended = false;
        this.clear();
    }

    private clear() {
        // Supersede any in-flight fetch — a late response must never resurrect rows after a
        // clear/stop (e.g. the watched extension was unlinked mid-fetch); `refresh()` re-checks both
        // this and `liveActive` before applying its response.
        this.issue++;
        this.callsRef.value = [];
        this.availabilityRef.value = 'available';
        this.knownCallIds.clear();
    }

    private async initialLoad() {
        await this.refresh();
        if (!this.liveActive) return; // stopped (or the watched set emptied) while the fetch was in flight
        this.invalidation.ready();
    }

    private keyFor(event: ITalkCallsEvent): string | string[] | null {
        if (event.event === 'presence-update') {
            const { targetType, extensionId } = event.payload;
            return targetType === 'extension' && extensionId && this.watchedIds.value.includes(extensionId) ? `presence:${extensionId}` : null;
        }
        if (event.event === 'call-state-updated' || event.event === 'call-ended') {
            return this.knownCallIds.has(event.payload.callId) ? `call:${event.payload.callId}` : null;
        }
        return null;
    }

    private async refresh() {
        const ids = this.watchedIds.value;
        if (!ids.length) {
            this.clear();
            return;
        }

        const linkedId = this.workstationExtension.blocksPersonalFallback.value ? this.workstationExtension.workstationExtensionId.value : null;
        // Ids the server has already told us it will never answer for are dropped from the request —
        // see `deniedNonLinkedIds`. The linked id is exempt (handled via the reload below instead).
        const requestIds = ids.filter(id => id === linkedId || !this.deniedNonLinkedIds.has(id));
        if (!requestIds.length) {
            this.clear();
            return;
        }

        const issue = ++this.issue;
        try {
            // The server caps `extensionIds` per request at MAX_LIVE_EXTENSION_IDS — split into chunks
            // and merge. In practice there's only ever one chunk (a handful of starred extensions, or
            // a single linked one); this exists so a tenant with an unusually large starred set
            // doesn't just 400.
            const results = await Promise.all(
                chunk(requestIds, MAX_LIVE_EXTENSION_IDS).map(extensionIds =>
                    TalkCdrApi.getCdrLiveList({ query: { extensionIds: extensionIds.join(',') } })
                )
            );
            if (issue !== this.issue || !this.liveActive) return; // superseded, or stopped while in flight

            const data = mergeLiveCallsResponses(results.map(r => dataFrom(r)).filter((d): d is ILiveCallsListResponse => !!d));
            if (!data) return;

            if (data.availability !== 'available') {
                // Never shown stale: FusionPBX (`unsupported`) or a transient outage (`unavailable`) both read as empty.
                this.availabilityRef.value = data.availability;
                this.callsRef.value = [];
                this.knownCallIds.clear();
                // 'unsupported' can't change for this watched set within the session — stop polling
                // for an answer that will never differ. `onWatchedIdsChanged` / `onTalkAccess` resume it.
                if (data.availability === 'unsupported' && !this.pollingSuspended) {
                    this.pollingSuspended = true;
                    this.invalidation.stop();
                }
                return;
            }

            this.availabilityRef.value = 'available';
            this.callsRef.value = data.calls;
            this.knownCallIds = new Set(data.calls.flatMap(c => [c.callId, c.rootCallId]));

            for (const deniedId of data.deniedExtensionIds) {
                if (deniedId !== linkedId) this.deniedNonLinkedIds.add(deniedId);
            }

            // A denied linked id means the link is stale server-side (extension deleted, access
            // narrowed) — reload it so the SPA heals on its own rather than keep asking for it.
            if (linkedId && data.deniedExtensionIds.includes(linkedId)) {
                DataCache.reloadCache('talkWorkstationExtension', true);
            }
        } catch (err) {
            if (issue !== this.issue) return;
            // The openapi client's onError hook already reports every request failure to Sentry —
            // don't double it here for a silent, automatic background refresh (unlike `decline` above).
            log.warn('Live-call refresh failed', err);
        }
    }
}

export const extensionLiveCallsMgr = new ExtensionLiveCallsManager();
